import { useMemo, useState, useEffect } from 'react';

// Two-level overhead picker: a top-level Category dropdown and a Sub-category
// dropdown filtered to the chosen category. The committed value is the leaf
// that time is booked against: the sub-category id, or the category's own id
// when it has no sub-categories. The Sub-category box only appears once a
// category that has sub-categories is chosen.
//
// Props:
//   categories  – full list from /overhead-categories (parents + children, each
//                 child carries parent_id). Parents have parent_id == null.
//   value       – currently selected leaf id (sub-category, or a top-level
//                 category with no sub-categories)
//   onChange    – (leafId) => void
//   className   – applied to each <select>
//   disabled    – disables both selects
export default function OverheadCategorySelect({ categories = [], value, onChange, className = 'form-input', disabled = false }) {
  const byName = (a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'en-GB', { sensitivity: 'base' });
  const parents  = useMemo(() => categories.filter(c => !c.parent_id).sort(byName), [categories]);
  const childMap = useMemo(() => {
    const m = {};
    for (const c of categories) if (c.parent_id) (m[c.parent_id] = m[c.parent_id] || []).push(c);
    return m;
  }, [categories]);

  // Derive the category of the current value so the Category box reflects it.
  // A top-level category logged directly is its own category.
  const valueParent = useMemo(() => {
    const leaf = categories.find(c => c.id === value);
    if (!leaf) return '';
    return leaf.parent_id || leaf.id;
  }, [categories, value]);

  const [parentId, setParentId] = useState(valueParent);
  // Item 41. This used to follow valueParent unconditionally, so the moment the
  // sub-category was cleared the CATEGORY box blanked itself too — re-opening
  // the category dropdown and choosing the same category wiped both boxes and
  // you had to start again. The category now only follows the value when the
  // value actually names a different category.
  useEffect(() => { if (valueParent) setParentId(valueParent); }, [valueParent]);

  const subs = [...(childMap[parentId] || [])].sort(byName);
  const showSubs = subs.length > 0;

  const onParent = (pid) => {
    // Re-picking the category you are already on is a no-op, not a reset.
    if (pid === parentId) return;
    setParentId(pid);
    const kids = childMap[pid] || [];
    // A category with no sub-categories is booked against directly. One with a
    // single sub-category (e.g. Other) auto-selects it; otherwise clear until
    // the user picks one.
    if (!pid) onChange('');
    else if (kids.length === 0) onChange(pid);
    else onChange(kids.length === 1 ? kids[0].id : '');
  };

  return (
    <div className={`grid grid-cols-1 gap-2 ${showSubs ? 'sm:grid-cols-2' : ''}`}>
      <select className={className} value={parentId} onChange={e => onParent(e.target.value)} disabled={disabled}>
        <option value="">Select category…</option>
        {parents.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      {showSubs && (
        <select className={className} value={value || ''} onChange={e => onChange(e.target.value)} disabled={disabled}>
          <option value="">Select sub-category…</option>
          {subs.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      )}
    </div>
  );
}
